/**
 * CrazyGamesRoom — the CENTRALIZED CrazyGames multiplayer room controller.
 *
 * This is the ONLY code path that talks to the platform's room API (via
 * the PlatformAdapter — the game code never calls `window.CrazyGames.SDK`
 * directly). It owns the room lifecycle:
 *
 *   enterRoom(lobby)   — first registration after the SERVER confirmed
 *                        the player is in the lobby (updateRoom).
 *   syncRoom(lobby)    — re-report on every authoritative lobby change
 *                        (join / leave / kick / countdown / full / game
 *                        end → the isJoinable value is recomputed from
 *                        the server state).
 *   setJoinable(bool)  — keep the room registered but flip joinability
 *                        (the match started: isJoinable=false, NO
 *                        leftRoom — the party survives the game).
 *   leaveRoom()        — leftRoom, ONLY when the player really left the
 *                        group (left / kicked / host closed / the server
 *                        says the lobby no longer exists / logout).
 *
 * The controller DEDUPLICATES: the SDK is only called when the room id or
 * the joinability value actually changes, so high-frequency state updates
 * never spam the platform.
 *
 * All calls are safe no-ops on standalone (the PlatformAdapter handles
 * that) and when the SDK `game` module is unavailable — including for
 * GUESTS on the CrazyGames subpage (the room layer is independent of
 * platform sign-in; auth is only about progress, not multiplayer).
 */

import { getPlatform, isLobbyJoinable, type MultiplayerRoom } from './PlatformAdapter';

// ─── The room the controller is currently reporting ──────────────────────────

interface TrackedRoom {
  /** The room's key (unique per lobby — `lobby:<id>` / `coop:<code>`). */
  key: string | null;
  /** The last full room payload (for setJoinable re-sends). */
  room: MultiplayerRoom | null;
  /** The last reported joinability (dedupe). */
  joinable: boolean | null;
}

const tracked: TrackedRoom = { key: null, room: null, joinable: null };

// ─── The centralized room API ────────────────────────────────────────────────

/**
 * Register the room (first time we are in this lobby). Called only after
 * the server confirmed membership (the canonical LobbyStateUpdate is in
 * hand) — never before.
 */
export function crazyGamesEnterRoom(room: MultiplayerRoom): void {
  const key = roomKey(room);
  if (tracked.key === key && tracked.joinable === room.isJoinable) return; // dedupe
  tracked.key = key;
  tracked.room = room;
  tracked.joinable = room.isJoinable;
  getPlatform().updateMultiplayerRoom(room);
}

/**
 * Re-report the room (joinability may have changed — computed from the
 * server state). Same room → no SDK call unless joinability flipped.
 */
export function crazyGamesSyncRoom(room: MultiplayerRoom): void {
  const key = roomKey(room);
  if (tracked.key === key && tracked.joinable === room.isJoinable) return; // dedupe
  tracked.key = key;
  tracked.room = room;
  tracked.joinable = room.isJoinable;
  getPlatform().updateMultiplayerRoom(room);
}

/**
 * Keep the CURRENT room registered but flip its joinability (the match
 * started → false; the group returns to the lobby → the next
 * crazyGamesSyncRoom restores it). NO leftRoom — the party survives the
 * game.
 */
export function crazyGamesSetJoinable(joinable: boolean): void {
  if (!tracked.room) return;
  if (tracked.joinable === joinable) return; // dedupe
  tracked.joinable = joinable;
  getPlatform().updateMultiplayerRoom({ ...tracked.room, isJoinable: joinable });
}

/**
 * Leave the room for good (leftRoom). Only when the player really left
 * the multiplayer group — never on a match start / component unmount.
 * Idempotent (no room tracked → no SDK call).
 */
export function crazyGamesLeaveRoom(): void {
  if (!tracked.room) return;
  tracked.key = null;
  tracked.room = null;
  tracked.joinable = null;
  getPlatform().leaveMultiplayerRoom();
}

/** The room the controller is currently reporting (null = none). */
export function crazyGamesCurrentRoom(): MultiplayerRoom | null {
  return tracked.room;
}

// ─── The state → room-action decision (pure, white-box testable) ────────────

/**
 * What the room layer should do for an authoritative client state:
 *  - 'sync'  → register/update the room (a lobby a player CAN join into)
 *  - 'leave' → leftRoom: the game is running (nobody can join — the
 *              platform's Invite Friends button must go away) or the room
 *              is gone for good
 *  - 'none'  → no room context at all (caller may no-op; the leave is
 *              idempotent, so callers typically leave for both 'leave' and
 *              'none')
 */
export type CrazyGamesRoomAction =
  | { kind: 'sync'; room: MultiplayerRoom }
  | { kind: 'leave' }
  | { kind: 'none' };

export function crazyGamesRoomActionForState(
  lobby: {
    id: string;
    code: string;
    state: string;
    players: unknown[];
    playerCapacity: number;
    exists: boolean;
  } | null,
  coop: { lobbyCode: string; memberId: string | null; exists: boolean } | null,
  inMatch: boolean
): CrazyGamesRoomAction {
  if (lobby && lobby.exists) {
    // IN_GAME: the match owns the party — there is no way to join an
    // ongoing game, so the room must NOT be advertised (isJoinable:false
    // alone does not deactivate the platform's Invite Friends button;
    // leftRoom does). The room returns on the next authoritative lobby
    // state (POST_GAME) via 'sync'.
    if (lobby.state === 'IN_GAME') return { kind: 'leave' };
    return { kind: 'sync', room: roomForOriginalLobby(lobby) };
  }
  if (!inMatch && coop && coop.exists) {
    return { kind: 'sync', room: roomForCoopLobby(coop) };
  }
  if (inMatch) return { kind: 'leave' };
  return { kind: 'none' };
}

/**
 * Compute the room payload for an ORIGINAL lobby from the authoritative
 * (server-supplied) client state.
 */
export function roomForOriginalLobby(lobby: {
  id: string;
  code: string;
  state: string;
  players: unknown[];
  playerCapacity: number;
}): MultiplayerRoom {
  return {
    roomId: lobby.id,
    isJoinable: isLobbyJoinable({
      state: lobby.state,
      playerCount: lobby.players.length,
      capacity: lobby.playerCapacity,
    }),
    inviteParams: { lobbyCode: lobby.code },
  };
}

/**
 * Compute the room payload for a CO-OP lobby (the same generic invite
 * params — the receiving side resolves the code through the unified
 * lobby-code resolver, which handles coop).
 */
export function roomForCoopLobby(coop: {
  lobbyCode: string;
  memberId: string | null;
}): MultiplayerRoom {
  return {
    roomId: coop.lobbyCode,
    isJoinable: coop.memberId === null, // the seat is free
    inviteParams: { lobbyCode: coop.lobbyCode },
  };
}

function roomKey(room: MultiplayerRoom): string {
  return `room:${room.roomId}`;
}

// ─── Testing ─────────────────────────────────────────────────────────────────

/** Reset the tracked room (tests only). */
export function _resetCrazyGamesRoomForTesting(): void {
  tracked.key = null;
  tracked.room = null;
  tracked.joinable = null;
}
